import { useEffect } from 'react'
import { useLocation } from 'react-router'

import { useLandingPage } from '@entities/LandingPage'

import { useHeaderStore } from '@widgets/header/model/headerStore'

import { getPromoLandingCode } from './promoHost'

/*
  "Site within a site": on <code>.promo.shop.anvar.kz the landing's header is
  the header for EVERY page on that host, not just the landing itself. A visitor
  who opens a product or the cart from a promo landing stays inside the promo
  skin instead of being dropped into the main site's chrome.

  The landing is fetched once at the layout level and cached by Apollo, so the
  landing page itself reuses the same result rather than issuing a second query.

  Ordering matters: the landing page sets the header from its own copy of the
  config once loaded, and this hook must not fight it. Both write the same
  variant and config for the same code, so whichever runs last is correct.

  Off a promo host the hook is inert — it does not touch the store at all, and
  the normal per-page header logic is untouched.
*/
export function usePromoSiteHeader () {
  const code = getPromoLandingCode()
  const location = useLocation()

  const { setLandingHeader, resetHeader } = useHeaderStore()

  const { landingPage } = useLandingPage({ code: code ?? undefined })

  const header = landingPage?.header

  useEffect(() => {
    if (!code || !header) {
      return
    }

    /*
      `hidden` is a landing's own choice for its own page — a chrome-less promo
      page. Applying it site-wide would leave the rest of the host with no
      header at all, so the promo skin falls back to the site header there.
    */
    const variant = header.variant ?? 'default'

    if (variant === 'custom') {
      setLandingHeader('landing', header)
    }
    else {
      setLandingHeader('default')
    }
    /*
      Re-applied on navigation: the landing page resets the store when it
      unmounts, which would otherwise strip the promo header from the next page.
    */
  }, [ code, header, location.pathname, setLandingHeader ])

  useEffect(() => {
    if (!code) {
      return
    }

    return () => resetHeader()
  }, [ code, resetHeader ])
}
